import { useEffect, useState, useRef, useContext, useCallback, useMemo } from 'react';
import braintree from 'braintree-web';
import { cn } from '@/utils/cn';
import { inputStyles } from '@/styles/input';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { getPaymentKey } from '@/utils/getPaymentKey';
import { CardBrandIcons } from '../PaymentInputs/CardBrandIcons';
import {
  BRAINTREE_FIELD_IDS,
  BRAINTREE_FIELD_CONFIG,
  BRAINTREE_STYLES,
  BRAINTREE_ERROR_CODES,
  BRAINTREE_HOSTED_FIELD_NAMES,
} from '@/constants/braintree';
import {
  waitForBraintreeDOMElements,
  createBraintreeClient,
  createBraintreeHostedFields,
  teardownBraintreeHostedFields,
  isBraintreeError,
  getNextBraintreeHostedFieldForAutoAdvance,
} from '@/utils/braintree';
import { Loader } from '../Common/Loader';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { shouldHideEmail } from '@/utils/conditionalRendering';
import { Email } from '../Common/Email';
import { useTranslate } from '@/hooks/useTranslate';
import type { HostedFieldsHostedFieldsFieldName } from 'braintree-web/hosted-fields';
import { logError } from '@/utils/logger';
import { ErrorLogs } from '@/constants/logs';
import { getLogInfoData } from '@/utils/logs';
import { isEmailValid } from '@/utils/email';
import { useReportPaymentMethodUnavailable } from '@/context/PaymentMethodAvailabilityContext';

export const BraintreeCardForm = () => {
  const context = useContext(CheckoutContext);
  const {
    customer,
    paymentKeys,
    paymentMethodsSettings,
    featureFlags,
    template,
    actions: { setIsFormValid },
    card: { setCardElementRef },
  } = context;
  const clientToken = useMemo(() => {
    const braintreeMethod = paymentMethodsSettings.find(
      (pm) => pm.provider === ProviderEnum.BRAINTREE && pm.paymentMethodName === PaymentMethodEnum.CREDIT_CARD
    );
    return getPaymentKey(braintreeMethod?.pspSubMidPk, paymentKeys.braintree, {
      provider: ProviderEnum.BRAINTREE,
      paymentMethod: PaymentMethodEnum.CREDIT_CARD,
    });
  }, [paymentMethodsSettings, paymentKeys.braintree]);
  const [isLoading, setIsLoading] = useState(true);
  // Hosted fields mount into our DOM containers and only then clear `isLoading`.
  // Nothing times that out, so every path that can never reach a mounted field
  // has to resolve to an explicit unavailable state instead of a spinner forever.
  const [hasLoadError, setHasLoadError] = useState(false);
  const setUnavailable = useReportPaymentMethodUnavailable();
  const [fieldsError, setFieldsError] = useState({
    number: null,
    expirationDate: null,
    cvv: null,
    cardholderName: null,
  });
  const [fieldsValid, setFieldsValid] = useState({
    number: false,
    expirationDate: false,
    cvv: false,
    cardholderName: false,
  });
  const previousFieldsValid = useRef({
    number: false,
    expirationDate: false,
    cvv: false,
    cardholderName: false,
  });
  const activeFieldRef = useRef<string | null>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hostedFieldsInstance = useRef<braintree.HostedFields | null>(null);
  const isInitialized = useRef(false);
  const cardElementRef = useRef<braintree.HostedFields | null>(null);
  const [isZipCodeValid, setIsZipCodeValid] = useState(false);
  const { t } = useTranslate();

  const getBraintreeErrorMessage = (
    fieldName: HostedFieldsHostedFieldsFieldName,
    fieldState: { isEmpty: boolean; isValid: boolean; isPotentiallyValid: boolean }
  ): string | null => {
    if (!fieldState.isValid) {
      switch (fieldName) {
        case 'number':
          if (fieldState.isEmpty) {
            return t('fieldErrors.incompleteCard');
          }
          return t('fieldErrors.invalidCard');
        case 'expirationDate':
          if (fieldState.isEmpty) {
            return t('fieldErrors.incompleteExpirationDate');
          }
          return t('fieldErrors.invalidExpirationDate');
        case 'cvv':
          if (fieldState.isEmpty) {
            return t('fieldErrors.incompleteSecurityCode');
          }
          return t('fieldErrors.invalidSecurityCode');
        case 'cardholderName':
          if (fieldState.isEmpty) {
            return t('fieldErrors.incompleteCardholderName');
          }
          return t('fieldErrors.incompleteCardholderName');
        default:
          return null;
      }
    }

    return null;
  };
  useEffect(() => {
    const allFieldsValid = Object.values(fieldsValid).every((valid) => valid);
    setIsFormValid(allFieldsValid && !isLoading && isZipCodeValid && isEmailValid(customer?.email));
  }, [fieldsValid, isLoading, isZipCodeValid, customer?.email, setIsFormValid]);

  useEffect(() => {
    if (!clientToken) {
      logError(ErrorLogs.braintreeCardFormError, getLogInfoData(context));
      setHasLoadError(true);
      setIsLoading(false);
      return;
    }

    if (isInitialized.current) {
      setIsLoading(false);
      return;
    }

    const initBraintree = async () => {
      try {
        const isDOMReady = await waitForBraintreeDOMElements();
        if (!isDOMReady) {
          logError(ErrorLogs.braintreeCardFormError, getLogInfoData(context));
          setHasLoadError(true);
          setIsLoading(false);
          return;
        }

        const clientInstance = await createBraintreeClient(clientToken);
        const hostedFields = await createBraintreeHostedFields(
          clientInstance,
          BRAINTREE_STYLES,
          BRAINTREE_FIELD_CONFIG
        );

        hostedFieldsInstance.current = hostedFields;
        cardElementRef.current = hostedFields;

        setCardElementRef(cardElementRef);

        hostedFields.on('validityChange', (event) => {
          const field = event.fields[event.emittedBy];
          const isValid = field.isValid;

          setFieldsValid((prev) => {
            previousFieldsValid.current = { ...prev, [event.emittedBy]: isValid };
            return {
              ...prev,
              [event.emittedBy]: isValid,
            };
          });

          if (isValid) {
            const nextField = getNextBraintreeHostedFieldForAutoAdvance(event.emittedBy);
            if (nextField) {
              setTimeout(() => hostedFields.focus(nextField), nextField === BRAINTREE_HOSTED_FIELD_NAMES.CVV ? 100 : 0);
            }
          }
        });

        hostedFields.on('focus', (event) => {
          // Clear any pending blur timeout to keep keyboard open
          if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
          }
          activeFieldRef.current = event.emittedBy;
        });

        hostedFields.on('blur', (event) => {
          // Delay clearing active field to allow focus transition to next field
          blurTimeoutRef.current = setTimeout(() => {
            activeFieldRef.current = null;
          }, 100);
          const fieldName = event.emittedBy;
          const field = event.fields[fieldName];
          const errorMessage = getBraintreeErrorMessage(fieldName, field);
          setFieldsError((prev) => ({
            ...prev,
            [fieldName]: errorMessage,
          }));
        });

        setIsLoading(false);
      } catch (err: unknown) {
        if (isBraintreeError(err) && err.code === BRAINTREE_ERROR_CODES.DUPLICATE_IFRAME) {
          console.warn('Duplicate iframe detected, skipping...');
          setIsLoading(false);
          return;
        }

        logError(ErrorLogs.braintreeCardFormError, getLogInfoData(context));
        setHasLoadError(true);
        setIsLoading(false);
        isInitialized.current = false;
      }
    };

    initBraintree();

    return () => {
      teardownBraintreeHostedFields(hostedFieldsInstance.current);
      hostedFieldsInstance.current = null;
      cardElementRef.current = null;
    };
  }, [clientToken, cardElementRef, setCardElementRef]);

  const handleTaxValidationChange = useCallback((isValid: boolean) => {
    setIsZipCodeValid(isValid);
  }, []);

  if (hasLoadError) {
    setUnavailable?.(true);
  }

  return (
    <div className="flex flex-col gap-2">
      <div id={BRAINTREE_FIELD_IDS.CARDHOLDER_NAME} className={cn(inputStyles, 'w-full')} />
      <div className="relative">
        <div id={BRAINTREE_FIELD_IDS.CARD_NUMBER} className={cn(inputStyles, 'w-full h-11')} />
        <CardBrandIcons />
        {fieldsError.number && <div className="text-xs text-red-500">{fieldsError.number}</div>}
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex flex-col gap-1 flex-1">
          <div id={BRAINTREE_FIELD_IDS.EXPIRATION_DATE} className={cn(inputStyles, 'w-full h-11')}></div>
          {fieldsError.expirationDate && <div className="text-xs text-red-500">{fieldsError.expirationDate}</div>}
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <div id={BRAINTREE_FIELD_IDS.CVV} className={cn(inputStyles, 'w-full h-11')}></div>
          {fieldsError.cvv && <div className="text-xs text-red-500">{fieldsError.cvv}</div>}
        </div>
      </div>
      <InputTaxComponent onValidate={handleTaxValidationChange} />
      {!shouldHideEmail(featureFlags, customer, template.features) && (
        <div className="w-full">
          <Email />
        </div>
      )}
      {isLoading && <Loader />}
    </div>
  );
};
